import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MessageCircle, ExternalLink, Calendar, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR, enUS, es } from "date-fns/locale";

const DATE_LOCALES = { pt: ptBR, en: enUS, es };

export default function Cobranca() {
  const { t, lang, fmtCurrency } = useT();
  const dateLocale = DATE_LOCALES[lang] || enUS;
  const [items, setItems] = useState([]);
  const [generating, setGenerating] = useState({});
  const [generated, setGenerated] = useState({});

  const load = async () => {
    const { data } = await api.get("/billing/scheduled");
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const generate = async (rentalId) => {
    setGenerating((g) => ({ ...g, [rentalId]: true }));
    try {
      const { data } = await api.post("/billing/checkout-link", {
        rental_id: rentalId,
        origin_url: window.location.origin,
      });
      setGenerated((g) => ({ ...g, [rentalId]: data }));
      toast.success(t("billing.linkGenerated"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGenerating((g) => ({ ...g, [rentalId]: false })); }
  };

  return (
    <div className="space-y-6" data-testid="cobranca-page">
      <div>
        <div className="overline">{t("billingPage.title", { n: items.length })}</div>
        <p className="text-sm text-zinc-500 mt-1">{t("billingPage.subtitle")}</p>
      </div>

      {items.length === 0 ? (
        <div className="border border-zinc-200 p-16 text-center">
          <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-4" />
          <div className="overline mb-2">{t("billingPage.emptyTitle")}</div>
          <p className="text-sm text-zinc-500">{t("billingPage.emptySub")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const overdue = it.hours_until < 0;
            const tone = overdue ? "border-red-300 bg-red-50/40" : "border-amber-300 bg-amber-50/40";
            const g = generated[it.rental_id];
            return (
              <div key={it.rental_id} className={`border ${tone} p-5`} data-testid={`billing-row-${it.rental_id}`}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                  <div>
                    <div className="overline">{t("rentals.customer")}</div>
                    <div className="font-semibold">{it.customer?.name}</div>
                    <div className="text-xs text-zinc-500 font-mono">{it.customer?.phone}</div>
                  </div>
                  <div>
                    <div className="overline">{t("rentals.vehicle")}</div>
                    <div className="font-semibold">{it.car?.model}</div>
                    <div className="text-xs text-zinc-500 font-mono">{it.car?.plate}</div>
                  </div>
                  <div>
                    <div className="overline">{overdue ? t("billingPage.overdueBy") : t("billingPage.dueIn")}</div>
                    <div className={`font-semibold inline-flex items-center gap-1.5 ${overdue ? "text-red-700" : "text-amber-700"}`}>
                      {overdue ? <AlertTriangle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                      {Math.abs(Math.round(it.hours_until))}h
                    </div>
                    <div className="text-xs text-zinc-500 font-mono inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {format(parseISO(it.next_billing_date), "dd MMM yyyy", { locale: dateLocale })}
                    </div>
                  </div>
                  <div className="text-right space-y-2">
                    <div className="font-display font-black text-2xl tracking-tighter">{fmtCurrency(it.amount)}</div>
                    {!g ? (
                      <Button onClick={() => generate(it.rental_id)} disabled={generating[it.rental_id]}
                              className="rounded-none bg-[#25D366] hover:bg-[#1FAE54] text-white"
                              data-testid={`generate-${it.rental_id}`}>
                        <MessageCircle className="w-4 h-4 mr-2" />
                        {generating[it.rental_id] ? "..." : t("billing.chargeViaWhatsapp")}
                      </Button>
                    ) : (
                      <div className="flex gap-2 justify-end flex-wrap">
                        {g.whatsapp_url && (
                          <a href={g.whatsapp_url} target="_blank" rel="noreferrer"
                             className="inline-flex items-center gap-1 bg-[#25D366] hover:bg-[#1FAE54] text-white px-3 py-2 text-xs"
                             data-testid={`send-wa-${it.rental_id}`}>
                            <MessageCircle className="w-3 h-3" /> {t("billing.openWhatsapp")}
                          </a>
                        )}
                        <a href={g.checkout_url} target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-1 border border-zinc-300 hover:border-zinc-500 px-3 py-2 text-xs">
                          <ExternalLink className="w-3 h-3" /> {t("billing.previewLink")}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                {g && (
                  <pre className="text-xs bg-white border border-zinc-200 p-3 mt-3 whitespace-pre-wrap font-mono text-zinc-700 max-h-32 overflow-y-auto">{g.whatsapp_message}</pre>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
